import React from 'react';

import { Customer, CustomerAuthLevel } from 'models/Customer';
import { DialogueCardData } from 'models/DialogCard';
import { useAppDispatch, useAppSelector } from 'store/store';

import { useCurrentArticle, useFirstArticle } from '../../hooks/shipment.hooks';
import {
  getAttemptedDeliveryCard,
  getAwaitingCollectionCard,
  getDeliveryOptionsCard,
  getFailedRedirectionCard,
  getInternationalParcelCard,
  getLateEddCard,
  getLatestEventCard,
  getPendingExplainerCard,
  getTimeWindowCard,
} from '../cards';

export const useDialogueCard = () => {
  const currentArticle = useCurrentArticle();
  const firstArticle = useFirstArticle();
  const collectionPoint = useAppSelector((state) => state.collectionPoint);
  const deliveryEstimate = useAppSelector((state) => state.deliveryEstimate);
  const safeDropPreference = useAppSelector((state) => state.deliveryPreference);
  const customerData = useAppSelector((state) => state.customer.data);
  const { knownToken = '', isAuthenticated = false } = (customerData as Customer) || {};
  const dispatch = useAppDispatch();

  const [isRedirectModalOpen, setRedirectModal] = React.useState(false);

  if (!currentArticle || safeDropPreference.loading || collectionPoint.loading) return null;

  let dialogueCard: DialogueCardData | null;

  const internationalTrackingLink = currentArticle.internationalTracking?.href;
  if (internationalTrackingLink) return getInternationalParcelCard(internationalTrackingLink);

  dialogueCard = getTimeWindowCard(currentArticle.timeWindow);
  if (dialogueCard) return dialogueCard;

  const { articleId, events, collectionInstruction, redirectStatus, address, previousDelivery } = currentArticle;
  const latestEvent = events?.[0] || null;

  dialogueCard = getAwaitingCollectionCard(
    isAuthenticated,
    articleId,
    latestEvent,
    collectionInstruction,
    address?.addressType,
    redirectStatus
  );
  if (dialogueCard) return dialogueCard;

  dialogueCard = getAttemptedDeliveryCard(latestEvent, collectionPoint.data);
  if (dialogueCard) return dialogueCard;

  const latestEdd = deliveryEstimate.calculatedEdd?.onDate ?? deliveryEstimate.latestEdd ?? null;
  dialogueCard = getLateEddCard(latestEdd, articleId);
  if (dialogueCard) return dialogueCard;

  const latestMilestone = latestEvent?.milestone ?? null;
  dialogueCard = getFailedRedirectionCard(redirectStatus, latestMilestone, previousDelivery);
  if (dialogueCard) return dialogueCard;

  dialogueCard = getLatestEventCard(latestEvent?.eventCode || null);
  if (dialogueCard) return dialogueCard;

  const { atlEligible, atlLocation, atlStatus, userOwnership, safeDropIneligibility } = currentArticle;
  const { articleRedirect } = firstArticle;
  let authLevel = CustomerAuthLevel.ANONYMOUS;
  if (knownToken) authLevel = CustomerAuthLevel.KNOWN;
  if (isAuthenticated) authLevel = CustomerAuthLevel.AUTHENTICATED;

  dialogueCard = getDeliveryOptionsCard(
    authLevel,
    atlEligible,
    articleRedirect,
    articleId,
    isRedirectModalOpen,
    setRedirectModal,
    dispatch,
    atlLocation,
    safeDropPreference.data,
    userOwnership,
    atlStatus,
    safeDropIneligibility?.code
  );
  if (dialogueCard) return dialogueCard;

  dialogueCard = getPendingExplainerCard(latestMilestone);

  return dialogueCard ?? { type: null, analyticsId: 'null' };
};
